import { memo, useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react'
import { OverlayScrollbarsComponent, type OverlayScrollbarsComponentRef } from 'overlayscrollbars-react'
import 'overlayscrollbars/overlayscrollbars.css'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { loadAvatarCategories, loadAvatars, searchAvatars, getAvatarImageUrl } from '@/src/renderer/lib/supabase/avatars'
import type { AvatarCategory, Avatar } from '@/src/renderer/lib/db/types'
import type { AvatarPickerPopoverProps, CategoryIconMap } from './AvatarPickerPopover.types'

// Import all category icons
import { AvatarNavbarFacesEmotionsIcon } from '@/src/renderer/components/icons/AvatarNavbarFacesEmotionsIcon'
import { AvatarNavbarPeopleIcon } from '@/src/renderer/components/icons/AvatarNavbarPeopleIcon'
import { AvatarNavbarFamousPersonalitiesIcon } from '@/src/renderer/components/icons/AvatarNavbarFamousPersonalitiesIcon'
import { AvatarNavbarAnimalsIcon } from '@/src/renderer/components/icons/AvatarNavbarAnimalsIcon'
import { AvatarNavbarFantasyMonstersIcon } from '@/src/renderer/components/icons/AvatarNavbarFantasyMonstersIcon'
import { AvatarNavbarSciFiIcon } from '@/src/renderer/components/icons/AvatarNavbarSciFiIcon'
import { AvatarNavbarFoodDrinksIcon } from '@/src/renderer/components/icons/AvatarNavbarFoodDrinksIcon'
import { AvatarNavbarVehiclesIcon } from '@/src/renderer/components/icons/AvatarNavbarVehiclesIcon'
import { AvatarNavbarSportsActivitiesIcon } from '@/src/renderer/components/icons/AvatarNavbarSportsActivitiesIcon'
import { AvatarNavbarGamingTechnologyIcon } from '@/src/renderer/components/icons/AvatarNavbarGamingTechnologyIcon'
import { AvatarSearchbarSearchIcon } from '@/src/renderer/components/icons/AvatarSearchbarSearchIcon'
import { ProfilePictureBackground } from '@/src/renderer/components/icons/ProfilePictureBackground'

import './AvatarPickerPopover.scss'

/**
 * Pure positioning helper used by the layout-effect below. Given the
 * trigger button's viewport-relative bottom edge and the picker's
 * natural height, returns the vertical shift (in px, ≤ 0) required to
 * keep the picker inside the viewport — or 0 if it already fits.
 *
 * When the host (preferences dialog, setup page) is too short to fit
 * the full picker beneath the button, we slide the picker up by the
 * overflow amount; if the host is so short that the picker would then
 * clip the viewport top, we clamp so the picker's top edge sits at
 * `padding` from the viewport top. This is the same shift-into-viewport
 * behaviour Floating UI's `shift` middleware gives the TaskDropdown.
 *
 * Exported for unit testing.
 */
export function computeVerticalShift({
  anchorBottom,
  containerHeight,
  viewportHeight,
  margin = 4,
  padding = 32,
}: {
  anchorBottom: number
  containerHeight: number
  viewportHeight: number
  margin?: number
  padding?: number
}): number {
  const idealTop = anchorBottom + margin
  const idealBottom = idealTop + containerHeight
  if (idealBottom <= viewportHeight - padding) return 0
  let shift = (viewportHeight - padding) - idealBottom
  if (idealTop + shift < padding) {
    shift = padding - idealTop
  }
  return shift
}

// Map icon_emoji to icon components
// These emoji values match the database icon_emoji column
const ICON_MAP: CategoryIconMap = {
  '😊': AvatarNavbarFacesEmotionsIcon,      // Faces & Emotions
  '👤': AvatarNavbarPeopleIcon,             // People & Persons
  '🎭': AvatarNavbarFamousPersonalitiesIcon, // Famous Personalities
  '🐻': AvatarNavbarAnimalsIcon,            // Animals
  '✨': AvatarNavbarFantasyMonstersIcon,    // Fantasy & Monsters
  '🤖': AvatarNavbarSciFiIcon,              // Sci-Fi & Robots
  '🎮': AvatarNavbarGamingTechnologyIcon,   // Gaming & Technology
  '🍕': AvatarNavbarFoodDrinksIcon,         // Food & Drink
  '🚗': AvatarNavbarVehiclesIcon,           // Vehicles
  '⚽': AvatarNavbarSportsActivitiesIcon,   // Sports & Activities
}

function AvatarPickerPopoverImpl({
  isOpen,
  onClose,
  onSelectAvatar,
  selectedAvatarId,
  align = 'left',
}: AvatarPickerPopoverProps) {
  const { t, i18n } = useTranslation()

  // State
  const [categories, setCategories] = useState<AvatarCategory[]>([])
  const [avatars, setAvatars] = useState<Avatar[]>([])
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filteredAvatars, setFilteredAvatars] = useState<Avatar[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loadedCategoriesCount, setLoadedCategoriesCount] = useState(1) // Start with 1 category
  const [isAnimating, setIsAnimating] = useState(false) // For fade-in animation
  const [isManualScroll, setIsManualScroll] = useState(false) // Track manual category clicks
  const [isInitialLoad, setIsInitialLoad] = useState(true) // Track initial progressive loading
  const [isScrolling, setIsScrolling] = useState(false) // Track scrolling to disable hover
  // Category to scroll to once its title ref is mounted (set after data load
  // when `selectedAvatarId` falls in a known category). Cleared by the
  // useLayoutEffect below once the scroll has run.
  const [pendingScrollCategoryId, setPendingScrollCategoryId] = useState<string | null>(null)

  // Refs
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const overlayScrollbarsRef = useRef<OverlayScrollbarsComponentRef>(null)
  const categoryTitleRefs = useRef<{ [key: string]: HTMLDivElement | null }>({}) // Refs to sticky titles
  const popoverRef = useRef<HTMLDivElement>(null)

  // Vertical shift applied when the host is too short to fit the full
  // picker beneath the trigger button. 0 in the common case — only the
  // tight-viewport branch sets a non-zero translate. See
  // `computeVerticalShift` above for the maths.
  const [shiftY, setShiftY] = useState(0)

  useLayoutEffect(() => {
    if (!isOpen) {
      setShiftY(0)
      return
    }
    const compute = () => {
      const popover = popoverRef.current
      const anchor = popover?.parentElement
      if (!popover || !anchor) return
      // The trigger is whichever sibling of the picker the host renders
      // first (typically the "Profilbild ändern" button). Falling back to
      // the anchor itself keeps the shift sensible if the host renders
      // only the picker.
      const trigger = Array.from(anchor.children).find((el) => el !== popover) as HTMLElement | undefined
      const triggerRect = (trigger ?? anchor).getBoundingClientRect()
      const containerEl = popover.firstElementChild as HTMLElement | null
      const containerHeight = containerEl?.offsetHeight || 410
      setShiftY(computeVerticalShift({
        anchorBottom: triggerRect.bottom,
        containerHeight,
        viewportHeight: window.innerHeight,
      }))
    }
    compute()
    window.addEventListener('resize', compute)
    return () => window.removeEventListener('resize', compute)
  }, [isOpen])

  // Close on outside click. The host's trigger button sits *outside* the
  // picker's DOM subtree, so a click on it would otherwise count as
  // "outside" and immediately close the just-opened picker. We delay the
  // listener by one tick so the opening pointer-up doesn't trigger it,
  // and we ignore any click whose target is the trigger button itself
  // (detected by walking up to find the closest `position: relative`
  // anchor wrapper the host renders us in).
  useEffect(() => {
    if (!isOpen) return
    let installed = false
    const armId = setTimeout(() => { installed = true }, 0)

    const handle = (e: MouseEvent) => {
      if (!installed) return
      const popoverEl = popoverRef.current
      if (!popoverEl) return
      const target = e.target as Node
      if (popoverEl.contains(target)) return
      // The relative anchor wraps both the trigger button and the picker,
      // so any click within the same anchor (e.g. on the trigger button
      // itself) is treated as "inside".
      const anchor = popoverEl.parentElement
      if (anchor?.contains(target)) return
      onClose()
    }
    document.addEventListener('mousedown', handle)
    return () => {
      clearTimeout(armId)
      document.removeEventListener('mousedown', handle)
    }
  }, [isOpen, onClose])

  // Scroll to the pre-selected category as soon as its title is in the DOM.
  // useLayoutEffect runs after React's commit phase but before paint, so the
  // user sees the picker already positioned at the right spot instead of
  // landing at the top and jumping. If the title ref isn't populated on the
  // first commit (progressive loading hasn't rendered it yet), the effect
  // re-runs when `loadedCategoriesCount` advances.
  //
  // We deliberately do NOT use `Element.scrollIntoView` here: that walks up
  // every scrollable ancestor and scrolls each one to bring the target into
  // view, which would also scroll the enclosing Dialog overlay when the
  // picker opens. Instead we scroll the OverlayScrollbars viewport
  // directly.
  useLayoutEffect(() => {
    if (!pendingScrollCategoryId) return
    const osInstance = overlayScrollbarsRef.current?.osInstance?.()
    const titleEl = categoryTitleRefs.current[pendingScrollCategoryId]
    const sectionEl = titleEl?.parentElement
    const viewport = osInstance?.elements().viewport
    if (!sectionEl || !viewport || !osInstance) return

    // Two rAFs give OverlayScrollbars enough time to (1) observe the
    // post-`display: none` resize, (2) update its internal scrollHeight,
    // before we measure. Forcing `osInstance.update(true)` at the start
    // of frame 2 covers the case where OS hasn't reacted to the resize
    // yet — the explicit update flushes measurements synchronously.
    // Without this defer, the first-open scroll lands short because the
    // viewport's scrollHeight is still stale.
    let rafInner = 0
    const rafOuter = requestAnimationFrame(() => {
      rafInner = requestAnimationFrame(() => {
        osInstance.update(true)
        // Reset to baseline before measuring. On re-open, the viewport
        // retains whatever scrollTop it had the last time the picker was
        // visible — and at that scrollTop, the sticky-positioned section
        // title is glued to the viewport top, which makes any bounding
        // rect inside the viewport read as "near the top" even though
        // the natural document position is further down. Resetting
        // unstucks all sticky titles so rects reflect natural layout,
        // then we compute the jump distance from 0.
        viewport.scrollTop = 0
        // Anchor on the *selected avatar tile*, not the section. Big
        // categories have many rows; if the user picked a tile near the
        // bottom of the section, anchoring on the section title lands
        // the scroll at the top of the section and leaves the picked
        // tile far below the visible viewport.
        const selectedTile = viewport.querySelector<HTMLElement>(
          '.avatar-picker-popover__avatar.selected'
        )
        const viewportRect = viewport.getBoundingClientRect()
        const titleHeight = titleEl ? titleEl.getBoundingClientRect().height : 0
        if (selectedTile && sectionEl.contains(selectedTile)) {
          const tileRect = selectedTile.getBoundingClientRect()
          // Park the tile one full row below the (about-to-stick) title
          // — the row that would otherwise be just above the selected
          // tile stays visible, so the user immediately reads "there's
          // more above" instead of mistaking the selected tile for the
          // top of the catalog. `Math.max(0, …)` clamps when the tile
          // is genuinely in the very first row (then scrollTop=0 is
          // the natural truth).
          const gap = 12
          const avatarRowHeight = 48 + 8 // tile size + grid gap, see SCSS
          const desiredOffsetFromViewportTop = titleHeight + gap + avatarRowHeight
          const targetScrollTop = Math.max(
            0,
            (tileRect.top - viewportRect.top) - desiredOffsetFromViewportTop
          )
          viewport.scrollTo({ top: targetScrollTop, behavior: 'auto' })
        } else {
          // Fallback: no tile match (e.g. selectedAvatarId no longer in
          // catalogue) — at least scroll the section title into view.
          const sectionRect = sectionEl.getBoundingClientRect()
          viewport.scrollTo({
            top: Math.max(0, sectionRect.top - viewportRect.top),
            behavior: 'auto',
          })
        }
        setPendingScrollCategoryId(null)
      })
    })
    return () => {
      cancelAnimationFrame(rafOuter)
      if (rafInner) cancelAnimationFrame(rafInner)
    }
  }, [pendingScrollCategoryId, loadedCategoriesCount])

  // Detect active category based on scroll position and sticky titles
  useEffect(() => {
    if (!overlayScrollbarsRef.current) return

    const osInstance = overlayScrollbarsRef.current.osInstance?.()
    const viewport = osInstance?.elements().viewport
    if (!viewport) return

    let scrollTimeout: NodeJS.Timeout

    const handleScroll = () => {
      // Set scrolling state to disable hover
      setIsScrolling(true)
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => setIsScrolling(false), 150)

      // Don't update during manual scroll, initial load, or search
      if (isManualScroll || isInitialLoad || searchQuery) return

      // Find which title is currently closest to the top (sticky position)
      let closestCategoryId: string | null = null
      let closestDistance = Infinity

      const viewportTop = viewport.getBoundingClientRect().top

      for (const [categoryId, titleElement] of Object.entries(categoryTitleRefs.current)) {
        if (!titleElement) continue

        const titleRect = titleElement.getBoundingClientRect()
        const distanceFromTop = Math.abs(titleRect.top - viewportTop)

        // Find the title closest to the top of the viewport (sticky position)
        if (distanceFromTop < closestDistance) {
          closestDistance = distanceFromTop
          closestCategoryId = categoryId
        }
      }

      if (closestCategoryId && closestCategoryId !== activeCategory) {
        setActiveCategory(closestCategoryId)
      }
    }

    // Run immediately when initial load completes
    if (!isInitialLoad) {
      handleScroll()
    }

    // Listen for scroll events
    viewport.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      viewport.removeEventListener('scroll', handleScroll)
      clearTimeout(scrollTimeout)
    }
  }, [isManualScroll, isInitialLoad, searchQuery, activeCategory, loadedCategoriesCount])

  // Disable initial load flag after progressive loading completes
  useEffect(() => {
    if (loadedCategoriesCount >= categories.length && categories.length > 0 && isInitialLoad) {
      // Add small delay to ensure all DOM elements are rendered
      const flagTimer = setTimeout(() => setIsInitialLoad(false), 300)
      return () => clearTimeout(flagTimer)
    }
  }, [loadedCategoriesCount, categories.length, isInitialLoad])

  // Load categories and avatars on open
  useEffect(() => {
    if (isOpen) {
      setIsInitialLoad(true) // Reset initial load flag
      loadData()
      // Trigger animation after a brief delay
      const animateTimer = setTimeout(() => setIsAnimating(true), 10)
      return () => clearTimeout(animateTimer)
    } else {
      // Reset animation state when closed
      setIsAnimating(false)
    }
    // selectedAvatarId is read inside the render (highlight ring) — it must
    // not retrigger a full data reload, otherwise picking an avatar refetches
    // the catalog every time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Progressive loading: Load more categories after initial render
  useEffect(() => {
    if (isOpen && categories.length > 0 && loadedCategoriesCount < categories.length) {
      const timer = setTimeout(() => {
        setLoadedCategoriesCount(prev => Math.min(prev + 2, categories.length))
      }, 100) // Load 2 more categories every 100ms

      return () => clearTimeout(timer)
    }
  }, [isOpen, categories.length, loadedCategoriesCount])

  /**
   * Search avatars by query. Errors are caught locally so a transient
   * Supabase/network failure doesn't leave the grid in an inconsistent
   * "stale results" state — we fall back to an empty list and the
   * "no results" copy in the UI takes over.
   *
   * Memoized on `avatars` + the resolved language so the search effect
   * below doesn't re-create the closure every render. Defined ABOVE
   * the effect that depends on it — useCallback returns a `const` that
   * is TDZ-bound, so referencing it in a deps array before this line
   * throws "Cannot access 'handleSearch' before initialization".
   */
  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setFilteredAvatars(avatars)
      return
    }
    // i18next can return locale codes like `de-DE` — normalize to the
    // two-letter form `searchAvatars` accepts.
    const language: 'de' | 'en' = i18n.language.startsWith('de') ? 'de' : 'en'
    try {
      const results = await searchAvatars(query, language)
      setFilteredAvatars(results)
    } catch (err) {
      console.error('[AvatarPicker] search failed:', err)
      setFilteredAvatars([])
    }
  }, [avatars, i18n.language])

  // Handle search
  useEffect(() => {
    if (searchQuery) {
      handleSearch(searchQuery)
    } else {
      setFilteredAvatars(avatars)
    }
  }, [searchQuery, avatars, handleSearch])

  /**
   * Load categories and avatars from API
   */
  const loadData = async () => {
    try {
      // Only show loading if we don't have data yet
      const shouldShowLoading = categories.length === 0 && avatars.length === 0
      if (shouldShowLoading) {
        setIsLoading(true)
      }

      const [loadedCategories, loadedAvatars] = await Promise.all([
        loadAvatarCategories(),
        loadAvatars(),
      ])

      setCategories(loadedCategories)
      setAvatars(loadedAvatars)
      setFilteredAvatars(loadedAvatars)

      // If we opened with a pre-selected avatar (e.g. re-editing a profile
      // picture from Settings), jump straight to its category instead of
      // landing at the top — otherwise the user has to scroll to find what
      // they already had. Skip the smooth scroll: the picker is still
      // fading in, so an instant scroll just looks "pre-positioned".
      let preselectedCategoryId: string | null = null
      if (selectedAvatarId) {
        const selectedAvatar = loadedAvatars.find((a) => a.id === selectedAvatarId)
        if (selectedAvatar) {
          const idx = loadedCategories.findIndex((c) => c.id === selectedAvatar.category_id)
          if (idx >= 0) {
            preselectedCategoryId = selectedAvatar.category_id
            // Skip progressive loading entirely and render every category
            // immediately. Rendering only up to the target index leaves the
            // OverlayScrollbars viewport with a `scrollHeight` that's only
            // marginally larger than `clientHeight`, so the desired
            // scroll-to-section position gets clamped to `maxScroll`
            // and the section's avatars end up below the visible area.
            // Rendering everything gives the viewport enough room below
            // the target section for the natural scroll to land cleanly.
            setLoadedCategoriesCount(loadedCategories.length)
            setActiveCategory(selectedAvatar.category_id)
          }
        }
      }

      if (preselectedCategoryId) {
        // Trigger the layout-effect-driven scroll once the title is in the
        // DOM. See the useLayoutEffect on `pendingScrollCategoryId`.
        setPendingScrollCategoryId(preselectedCategoryId)
      } else if (loadedCategories.length > 0 && !activeCategory) {
        // No pre-selection: keep historical first-category default.
        setActiveCategory(loadedCategories[0].id)
      }
    } catch (error) {
      console.error('[AvatarPicker] Failed to load data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Handle category icon click - scroll to category.
   *
   * We deliberately do not use `scrollIntoView` on the title element:
   * the title is `position: sticky; top: 0` so once the user has
   * scrolled past it, its rendered bounding box sits at the viewport's
   * top edge regardless of which category is actually in view. Asking
   * the browser to scroll a sticky-stuck element to "start" is a
   * no-op (it is already there), which makes the click feel broken.
   *
   * Instead we measure the title's parent `__category-section` — that
   * element is a plain block flow, so its bounding box reflects the
   * true document position. We then scroll the viewport by the delta
   * between its current top and the viewport's top, which works
   * symmetrically for upward and downward navigation.
   */
  const handleCategoryClick = (categoryId: string) => {
    setActiveCategory(categoryId)
    setSearchQuery('') // Clear search
    setIsManualScroll(true) // Disable intersection observer temporarily

    const titleElement = categoryTitleRefs.current[categoryId]
    const sectionElement = titleElement?.parentElement
    const osInstance = overlayScrollbarsRef.current?.osInstance?.()
    const viewport = osInstance?.elements().viewport
    if (!sectionElement || !viewport) return

    const sectionRect = sectionElement.getBoundingClientRect()
    const viewportRect = viewport.getBoundingClientRect()
    const targetScrollTop = viewport.scrollTop + (sectionRect.top - viewportRect.top)

    // Detect when smooth scroll finishes so the intersection observer
    // can take over again without overriding our chosen category.
    let scrollTimeout: NodeJS.Timeout
    const handleScrollEnd = () => {
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        setIsManualScroll(false)
        viewport.removeEventListener('scroll', handleScrollEnd)
      }, 150)
    }
    viewport.addEventListener('scroll', handleScrollEnd, { passive: true })

    viewport.scrollTo({ top: targetScrollTop, behavior: 'smooth' })
  }

  /**
   * Handle avatar click - select avatar and close
   */
  const handleAvatarClick = (avatar: Avatar) => {
    // Profile picture icon renders the selected avatar at ~71px → retina 142 → 256 bucket.
    const imageUrl = getAvatarImageUrl(avatar, 72) ?? ''
    onSelectAvatar(avatar.id, imageUrl)
    // Don't save scroll position when selecting (user might want to select again from same position)
    onClose()
  }

  /**
   * Clear search and scroll to top
   */
  const handleClearSearch = () => {
    setSearchQuery('')
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
    // Reset to first category
    if (categories.length > 0) {
      setActiveCategory(categories[0].id)
    }
  }

  /**
   * Get category name based on current language
   */
  const getCategoryName = (category: AvatarCategory): string => {
    return i18n.language === 'de' ? category.name_de : category.name_en
  }

  /**
   * Get icon component for category
   */
  const getCategoryIcon = (category: AvatarCategory) => {
    const IconComponent = ICON_MAP[category.icon_emoji]
    return IconComponent || AvatarNavbarFacesEmotionsIcon // Fallback
  }

  /**
   * Group filtered avatars by category once per data change instead of
   * re-filtering inside every render iteration (N×M during progressive load).
   */
  const avatarsByCategory = useMemo(() => {
    const map = new Map<string, Avatar[]>()
    for (const avatar of filteredAvatars) {
      const list = map.get(avatar.category_id)
      if (list) list.push(avatar)
      else map.set(avatar.category_id, [avatar])
    }
    return map
  }, [filteredAvatars])

  /**
   * Pre-resolve grid-tile image URLs once per avatar list change.
   * Grid tile is 48px and expands to 96px on hover → 192 retina → 256 bucket.
   */
  const avatarImageUrls = useMemo(() => {
    const map = new Map<string, string>()
    for (const avatar of filteredAvatars) {
      map.set(avatar.id, getAvatarImageUrl(avatar, 96) ?? '')
    }
    return map
  }, [filteredAvatars])

  // Positioning is pure CSS now — the picker is rendered as a child of a
  // `position: relative` wrapper that lives next to the trigger button.
  // See AvatarPickerPopover.scss `&__container` for the `top: 100%`/`left: 0`
  // anchor. The `buttonPosition` prop is kept for callsite compatibility
  // but is no longer consulted.

  // Rendered inline as a sibling of the trigger button (inside a
  // `position: relative` wrapper provided by the host). Avoids React
  // portals because Radix Dialog's `react-remove-scroll` blocks wheel
  // events on portal siblings and the transform-based DialogContent
  // hijacks the containing block of `position: fixed` descendants —
  // both made the previous portal approach unworkable.
  return (
    <div
      ref={popoverRef}
      className={`avatar-picker-popover avatar-picker-popover--align-${align} ${!isOpen ? 'avatar-picker-popover--hidden' : ''}`.trim()}
      style={shiftY !== 0 ? { transform: `translateY(${shiftY}px)` } : undefined}
    >
      {/* Popover Container */}
      <div
        className={`avatar-picker-popover__container ${isAnimating ? 'avatar-picker-popover__container--visible' : ''}`}
      >
        {/* 1. NavBar with Category Icons */}
        <div className="avatar-picker-popover__navbar">
          {isLoading ? (
            <div className="avatar-picker-popover__loading">
              <div className="avatar-picker-popover__spinner" />
            </div>
          ) : categories.length === 0 ? (
            <div className="avatar-picker-popover__loading">{t('avatarPicker.noCategoriesFound')}</div>
          ) : (
            categories.map((category) => {
              const IconComponent = getCategoryIcon(category)
              const isActive = activeCategory === category.id

              return (
                <div
                  key={category.id}
                  className={`avatar-picker-popover__category-icon ${isActive ? 'active' : ''}`}
                  onClick={() => handleCategoryClick(category.id)}
                  title={getCategoryName(category)}
                >
                  <IconComponent className="avatar-picker-popover__category-icon-svg" />
                  {isActive && <div className="avatar-picker-popover__category-icon-underline" />}
                </div>
              )
            })
          )}
        </div>

        {/* 2. SearchBar */}
        <div className="avatar-picker-popover__searchbar">
          <AvatarSearchbarSearchIcon />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('avatarPicker.searchPlaceholder')}
            className="avatar-picker-popover__search-input"
          />
          {searchQuery && (
            <button
              className="avatar-picker-popover__clear-button"
              onClick={handleClearSearch}
              aria-label={t('avatarPicker.clearSearch')}
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* 3. Avatar Listing with Scroll */}
        <OverlayScrollbarsComponent
          ref={overlayScrollbarsRef}
          element="div"
          options={{
            scrollbars: { autoHide: 'leave', autoHideDelay: 800 },
            overflow: { x: 'hidden', y: 'scroll' }, // Only vertical scroll, hide horizontal
          }}
          defer
          className={`avatar-picker-popover__listing ${isScrolling ? 'is-scrolling' : ''}`}
        >
          <div ref={scrollContainerRef}>
            {isLoading ? (
              <div className="avatar-picker-popover__loading">
                <div className="avatar-picker-popover__spinner" />
              </div>
            ) : searchQuery ? (
              // Search Results Mode
              <div className="avatar-picker-popover__search-results">
                {filteredAvatars.length === 0 ? (
                  <div className="avatar-picker-popover__no-results">
                    {t('avatarPicker.noResults')}
                  </div>
                ) : (
                  <div className="avatar-picker-popover__avatar-grid">
                    {filteredAvatars.map((avatar) => {
                      const isSelected = selectedAvatarId === avatar.id
                      const imageUrl = avatarImageUrls.get(avatar.id) ?? ''

                      return (
                        <div
                          key={avatar.id}
                          className={`avatar-picker-popover__avatar ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleAvatarClick(avatar)}
                          title={avatar.name}
                        >
                          {/* Squircle backdrop matches the preferences avatar
                              hover-zoom — hidden until :hover via SCSS. */}
                          <ProfilePictureBackground className="avatar-picker-popover__avatar-bg" />
                          <img src={imageUrl} alt={avatar.name} loading="lazy" decoding="async" />
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            ) : (
              // Category Mode (Lazy Loading)
              categories.slice(0, loadedCategoriesCount).map((category) => {
                const categoryAvatars = avatarsByCategory.get(category.id) ?? []
                if (categoryAvatars.length === 0) return null

                return (
                  <div
                    key={category.id}
                    className="avatar-picker-popover__category-section"
                  >
                    {/* Sticky Category Title */}
                    <div
                      ref={(el) => (categoryTitleRefs.current[category.id] = el)}
                      data-category-id={category.id}
                      className="avatar-picker-popover__category-title"
                    >
                      {getCategoryName(category)}
                    </div>

                    {/* Grid of Avatars */}
                    <div className="avatar-picker-popover__avatar-grid">
                      {categoryAvatars.map((avatar) => {
                        const isSelected = selectedAvatarId === avatar.id
                        const imageUrl = avatarImageUrls.get(avatar.id) ?? ''

                        return (
                          <div
                            key={avatar.id}
                            className={`avatar-picker-popover__avatar ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleAvatarClick(avatar)}
                            title={avatar.name}
                          >
                            <ProfilePictureBackground className="avatar-picker-popover__avatar-bg" />
                            <img src={imageUrl} alt={avatar.name} loading="lazy" decoding="async" />
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })
            )}
            {/* Loading indicator for progressive load */}
            {!searchQuery && loadedCategoriesCount < categories.length && (
              <div className="avatar-picker-popover__loading avatar-picker-popover__loading--inline">
                <div className="avatar-picker-popover__spinner" />
              </div>
            )}
          </div>
        </OverlayScrollbarsComponent>
      </div>
    </div>
  )
}

/**
 * The picker has a heavyweight render path (catalogue grid + nav rail +
 * search). `React.memo` with the default shallow comparator skips re-renders
 * when the parent re-renders without changing the picker's props.
 *
 * Callers must pass stable callback refs (`useCallback`) for `onClose` and
 * `onSelectAvatar` for the memo to actually skip — otherwise every parent
 * render still triggers a re-render here.
 */
export const AvatarPickerPopover = memo(AvatarPickerPopoverImpl)
